/**
 * Helper utilities for dealing with authentication flows
 * especially password reset and recovery tokens
 */

/**
 * Extracts and stores a recovery token from the URL hash
 * This is useful for password reset flows
 */
export function extractAndStoreRecoveryToken(): { accessToken: string | null, refreshToken: string | null } {
  try {
    // Use the new parsing utility with the current URL
    const { accessToken, refreshToken, type } = parseRecoveryTokensFromUrl(window.location.href);
    
    // Check if this is a recovery token
    if (!accessToken || type !== 'recovery') {
      console.log('No recovery token found in URL');
      return { accessToken: null, refreshToken: null };
    }
    
    console.log('Found recovery token in URL, extracting...');
    
    // Validate the token before storing
    const validationResult = validateJwtToken(accessToken);
    if (!validationResult.valid) {
      console.error(`Invalid token: ${validationResult.error}`);
      return { accessToken: null, refreshToken: null };
    }
    
    // Store in localStorage for persistence
    console.log('Storing recovery tokens in localStorage');
    localStorage.setItem('recovery_access_token', accessToken);
    
    if (refreshToken) {
      localStorage.setItem('recovery_refresh_token', refreshToken);
    }
    
    // Store also in sessionStorage for backup
    sessionStorage.setItem('recovery_access_token', accessToken);
    if (refreshToken) {
      sessionStorage.setItem('recovery_refresh_token', refreshToken);
    }
    
    return { accessToken, refreshToken };
  } catch (error) {
    console.error('Error extracting recovery token:', error);
    return { accessToken: null, refreshToken: null };
  }
}

/**
 * Gets the stored recovery tokens from localStorage or sessionStorage
 */
export function getStoredRecoveryTokens(): { accessToken: string | null, refreshToken: string | null } {
  try {
    // First try localStorage
    let accessToken = localStorage.getItem('recovery_access_token');
    let refreshToken = localStorage.getItem('recovery_refresh_token');
    
    // If not found in localStorage, try sessionStorage as fallback
    if (!accessToken) {
      console.log('No token in localStorage, checking sessionStorage');
      accessToken = sessionStorage.getItem('recovery_access_token');
      refreshToken = sessionStorage.getItem('recovery_refresh_token');
      
      // If found in sessionStorage, copy to localStorage for persistence
      if (accessToken) {
        console.log('Found token in sessionStorage, copying to localStorage');
        localStorage.setItem('recovery_access_token', accessToken);
        if (refreshToken) {
          localStorage.setItem('recovery_refresh_token', refreshToken);
        }
      }
    }
    
    // Validate the token if found
    if (accessToken) {
      const validationResult = validateJwtToken(accessToken);
      if (!validationResult.valid) {
        console.error(`Stored token is invalid: ${validationResult.error}`);
        // Keep the token - it may still work for the API even if validation fails
      }
    }
    
    return { accessToken, refreshToken };
  } catch (error) {
    console.error('Error getting stored recovery tokens:', error);
    return { accessToken: null, refreshToken: null };
  }
}

/**
 * Clears the stored recovery tokens from localStorage and sessionStorage
 */
export function clearRecoveryTokens(): void {
  try {
    // Clear from localStorage
    localStorage.removeItem('recovery_access_token');
    localStorage.removeItem('recovery_refresh_token');
    
    // Also clear from sessionStorage
    sessionStorage.removeItem('recovery_access_token');
    sessionStorage.removeItem('recovery_refresh_token');
    
    console.log('Recovery tokens cleared from storage');
  } catch (error) {
    console.error('Error clearing recovery tokens:', error);
  }
}

/**
 * Extracts the user ID from a JWT token
 */
export function getUserIdFromToken(token: string): string | null {
  try {
    // Get the data part of the JWT (second part)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decode and parse
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const payload = JSON.parse(jsonPayload);
    return payload.sub || null;
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
}

/**
 * Parses recovery tokens from a URL
 * This works with either the hash or search parameters
 */
export function parseRecoveryTokensFromUrl(url: string): { accessToken: string | null, refreshToken: string | null, type: string | null } {
  try {
    // Check for hash-based format
    if (url.includes('#')) {
      const hashPart = url.split('#')[1];
      if (hashPart && hashPart.includes('type=recovery')) {
        const hashParams = new URLSearchParams(hashPart);
        return {
          accessToken: hashParams.get('access_token'),
          refreshToken: hashParams.get('refresh_token'),
          type: hashParams.get('type')
        };
      }
    }
    
    // Check for search parameter based format
    if (url.includes('?')) {
      const searchPart = url.split('?')[1];
      const searchParams = new URLSearchParams(searchPart);
      
      // Check for code-based flow (PKCE)
      const code = searchParams.get('code');
      if (code && searchParams.get('type') === 'recovery') {
        return {
          accessToken: null, // Code-based flow doesn't have direct tokens
          refreshToken: null,
          type: 'recovery-code'
        };
      }
    }
    
    return { accessToken: null, refreshToken: null, type: null };
  } catch (error) {
    console.error('Error parsing recovery tokens from URL:', error);
    return { accessToken: null, refreshToken: null, type: null };
  }
}

/**
 * Validates a JWT token
 */
export function validateJwtToken(token: string): { valid: boolean, error?: string, payload?: any } {
  try {
    // Parse the token to check its claims
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return { valid: false, error: 'Invalid token format - not a standard JWT' };
    }
    
    // Decode the payload (middle part)
    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const rawPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    const payload = JSON.parse(rawPayload);
    
    // Check expiration
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    
    if (expirationTime < currentTime) {
      return { 
        valid: false, 
        error: 'Token has expired', 
        payload
      };
    }
    
    // Check other essential claims
    if (!payload.sub) {
      return { 
        valid: false, 
        error: 'Token missing subject claim',
        payload
      };
    }
    
    return { 
      valid: true,
      payload
    };
  } catch (error) {
    return { 
      valid: false, 
      error: `Error parsing token: ${error}` 
    };
  }
} 